/**
 * Remoção de dados pessoais de payloads de observabilidade (**C4**, T0.12).
 *
 * O Sentry captura o contexto inteiro por padrão. Num produto que guarda data de nascimento, NIF e
 * IBAN, isso transforma a ferramenta de erro num **repositório secundário de dados pessoais** — fora
 * do RoPA, sem base legal e sem retenção definida. PII no Sentry é, em si, um dos gatilhos de
 * detecção de incidente.
 *
 * A mesma função serve ao logger: um log estruturado que imprime `email` em JSON é o mesmo
 * vazamento por outra porta.
 */

export const REDACTED = '[redacted]';

/**
 * Âncoras `^...$` de propósito: `email_verified` é booleano de estado, não o email, e redigi-lo
 * perderia informação útil sem proteger nada. Sem `g` — a flag global torna `test()` stateful
 * através de `lastIndex`, e chamadas alternadas passariam a devolver `false` indevidamente.
 */
export const PII_KEYS =
  /^(email|phone|birth_date|nif|iban|address|full_name|password|token)$/i;

/**
 * Redige PII **no lugar**, percorrendo objetos e arrays.
 *
 * Muta em vez de clonar porque o `beforeSend` do Sentry devolve o próprio evento; clonar perderia
 * campos não enumeráveis do objeto original.
 *
 * @param alvo Qualquer valor. Primitivos, `null` e `undefined` são ignorados sem erro.
 */
export function redact(alvo: unknown, vistos: WeakSet<object> = new WeakSet()): void {
  if (!alvo || typeof alvo !== 'object') return;

  // Guarda contra referência circular. Um laço infinito aqui faria o Sentry deixar de reportar
  // exatamente quando mais é necessário — um erro no handler de erro.
  if (vistos.has(alvo)) return;
  vistos.add(alvo);

  if (Array.isArray(alvo)) {
    for (const item of alvo) redact(item, vistos);
    return;
  }

  const registo = alvo as Record<string, unknown>;

  for (const [chave, valor] of Object.entries(registo)) {
    if (PII_KEYS.test(chave)) {
      registo[chave] = REDACTED;
    } else {
      redact(valor, vistos);
    }
  }
}
